// V2 Review & qualify page (Find-more top-up flow).
// Route: /v2/campaigns/:id/review
//
// Reads campaign.proposedProspects (populated by /find-prospects?mode=topup),
// shows a rail + detail layout scoped to this campaign, and on approve calls
// /add-prospects so leads join the existing campaign without touching its status.
//
// Simpler than the wizard ProspectsScreen — no manual-add modal, no channel copy
// helpers, no "existing" dimmed rows (those are hard-filtered server-side for topup).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft } from '@tabler/icons-react';
import useApi from '../hooks/useApi';
import { addProspectsToCampaign, fetchCampaign } from '../services/api';

const STRONG_FIT_THRESHOLD = 80;

function initialsFor(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function firstNameOf(name) {
  return String(name || '').trim().split(/\s+/)[0] || '';
}

function fitColorFor(score) {
  const v = Number(score || 0);
  if (v >= STRONG_FIT_THRESHOLD) return 'var(--mantine-color-sage-6)';
  if (v >= 60) return 'var(--mantine-color-sage-4)';
  return 'var(--aviary-muted)';
}

function FitCircle({ score }) {
  const v = Math.round(Number(score || 0));
  if (!v) return null;
  return (
    <Stack align="center" gap={0} style={{ flexShrink: 0 }}>
      <Box
        style={{
          width: 60,
          height: 60,
          borderRadius: '50%',
          border: `3px solid ${fitColorFor(v)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text fw={700} size="lg" style={{ color: 'var(--aviary-ink)', lineHeight: 1 }}>{v}</Text>
      </Box>
      <Text size="xs" fw={600} mt={4} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.5 }}>
        FIT
      </Text>
    </Stack>
  );
}

function ProspectRail({ prospect, isActive, isChecked, onSelect, onToggleCheck }) {
  const score = Math.round(Number(prospect.fitScore ?? prospect.score ?? 0));
  return (
    <UnstyledButton
      onClick={() => onSelect(prospect)}
      style={{
        display: 'block',
        width: '100%',
        padding: '10px 12px',
        borderRadius: 10,
        border: isActive
          ? '2px solid var(--mantine-color-sage-6)'
          : '1px solid var(--aviary-disabled)',
        background: isActive ? 'var(--mantine-color-sage-0)' : 'var(--aviary-surface)',
      }}
    >
      <Group gap={10} wrap="nowrap" align="center">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={(e) => { e.stopPropagation(); onToggleCheck(prospect); }}
          onClick={(e) => e.stopPropagation()}
          style={{ accentColor: 'var(--mantine-color-sage-6)', cursor: 'pointer' }}
        />
        <Avatar
          src={prospect.photoUrl || null}
          size={32}
          radius="xl"
          color="sage"
        >
          {initialsFor(prospect.name)}
        </Avatar>
        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)', lineHeight: 1.2 }} truncate>
            {prospect.name || '(no name)'}
          </Text>
          <Text size="xs" style={{ color: 'var(--aviary-muted)', lineHeight: 1.2 }} truncate>
            {[prospect.title, prospect.company].filter(Boolean).join(' · ') || '—'}
          </Text>
        </Stack>
        {score > 0 && (
          <Text size="sm" fw={700} style={{ color: fitColorFor(score), flexShrink: 0 }}>{score}</Text>
        )}
      </Group>
    </UnstyledButton>
  );
}

function DetailPanel({ prospect, notesDraft, onNotesChange }) {
  const whyText = String(prospect.coachNote || prospect.fitReason || '').trim();
  const opener = String(prospect.personalizedOpener || '').trim();
  const firstName = firstNameOf(prospect.name);
  const score = Math.round(Number(prospect.fitScore ?? prospect.score ?? 0));

  return (
    <Stack gap="lg">
      <Group align="flex-start" wrap="nowrap" gap="md">
        <Avatar
          src={prospect.photoUrl || null}
          size={64}
          radius="xl"
          color="sage"
        >
          {initialsFor(prospect.name)}
        </Avatar>
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Text fw={700} size="lg" style={{ color: 'var(--aviary-ink)', lineHeight: 1.2 }}>
            {prospect.name || '(no name)'}
          </Text>
          <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
            {[prospect.title, prospect.company].filter(Boolean).join(' · ') || '—'}
          </Text>
          {(prospect.city || prospect.country) && (
            <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
              {[prospect.city, prospect.country].filter(Boolean).join(', ')}
            </Text>
          )}
        </Stack>
        <FitCircle score={score} />
      </Group>

      {whyText && (
        <Stack gap={6}>
          <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>
            Why {firstName || 'this person'} is a good fit
          </Text>
          <Text size="sm" style={{ color: 'var(--aviary-ink)', lineHeight: 1.55 }}>
            {whyText}
          </Text>
        </Stack>
      )}

      {opener && (
        <Stack gap={6}>
          <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
            THEIR VERSION OF THE OPENER
          </Text>
          <Paper
            p="md"
            radius="md"
            style={{ background: 'var(--aviary-surface)', border: '1px solid var(--aviary-disabled)' }}
          >
            <Text size="sm" style={{ color: 'var(--aviary-ink)', lineHeight: 1.55, fontStyle: 'italic' }}>
              &ldquo;{opener}&rdquo;
            </Text>
          </Paper>
        </Stack>
      )}

      <Stack gap={6}>
        <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
          ADD COACHING NOTES
        </Text>
        <Textarea
          value={notesDraft}
          onChange={(e) => onNotesChange(prospect.externalId, e.currentTarget.value)}
          autosize
          minRows={2}
          placeholder="anything the agent should know…"
          styles={{
            input: {
              background: 'var(--aviary-surface)',
              border: '1px dashed var(--aviary-disabled)',
              borderRadius: 8,
              fontSize: 13.5,
              lineHeight: 1.5,
              color: 'var(--aviary-ink)',
            },
          }}
        />
      </Stack>
    </Stack>
  );
}

export default function AviaryV2CampaignReviewPage() {
  const api = useApi();
  const navigate = useNavigate();
  const { id: campaignId } = useParams();

  const [campaign, setCampaign] = useState(null);
  const [pending, setPending] = useState([]);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [notesByProspect, setNotesByProspect] = useState({});
  const [activeId, setActiveId] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const detail = await fetchCampaign(api, campaignId);
      const camp = detail?.campaign || null;
      setCampaign(camp);
      const list = Array.isArray(camp?.proposedProspects) ? camp.proposedProspects : [];
      setPending(list);
      // Preselect the strong-fit candidates by default.
      const strong = list
        .filter((p) => Number(p.fitScore ?? p.score ?? 0) >= STRONG_FIT_THRESHOLD)
        .map((p) => String(p.externalId));
      setSelectedIds(new Set(strong.length ? strong : list.map((p) => String(p.externalId))));
      if (list.length) setActiveId(String(list[0].externalId));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, [api, campaignId]);

  useEffect(() => { load(); }, [load]);

  const activeProspect = useMemo(() => (
    pending.find((p) => String(p.externalId) === activeId) || pending[0] || null
  ), [pending, activeId]);

  function toggleCheck(prospect) {
    const key = String(prospect.externalId);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function selectAllStrong() {
    const ids = pending
      .filter((p) => Number(p.fitScore ?? p.score ?? 0) >= STRONG_FIT_THRESHOLD)
      .map((p) => String(p.externalId));
    setSelectedIds(new Set(ids));
  }

  function onNotesChange(externalId, value) {
    setNotesByProspect((prev) => ({ ...prev, [String(externalId)]: value }));
  }

  function goBack() {
    if (campaign?.id) navigate(`/v2/campaigns/${campaign.id}`);
    else navigate('/v2/campaigns');
  }

  async function handleAddToCampaign() {
    if (!campaign?.id) return;
    const selected = pending
      .filter((p) => selectedIds.has(String(p.externalId)))
      .map((p) => ({
        ...p,
        manualCoachNotes: notesByProspect[String(p.externalId)] || p.manualCoachNotes || null,
      }));
    if (!selected.length) return;
    setAdding(true);
    try {
      const result = await addProspectsToCampaign(api, campaign.id, { selectedProspects: selected });
      notifications.show({
        title: `Added ${result?.imported ?? selected.length} to campaign`,
        message: result?.skipped > 0 ? `${result.skipped} were duplicates and skipped.` : 'Back to campaign.',
        color: 'sage',
      });
      goBack();
    } catch (err) {
      notifications.show({
        title: 'Could not add to campaign',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setAdding(false);
    }
  }

  if (error) {
    return (
      <Center py={80} px="lg">
        <Stack gap="sm" align="center">
          <Text fw={600} c="red">Couldn&apos;t load the review batch</Text>
          <Text size="sm" c="dimmed">{error}</Text>
          <UnstyledButton onClick={goBack}><Text c="sage">← Back to campaign</Text></UnstyledButton>
        </Stack>
      </Center>
    );
  }
  if (!campaign) return <Center py={80}><Loader color="sage" /></Center>;

  if (pending.length === 0) {
    return (
      <Box style={{ padding: '32px 24px 120px', maxWidth: 1080, margin: '0 auto' }}>
        <Stack gap="lg" align="center">
          <UnstyledButton onClick={goBack} style={{ alignSelf: 'flex-start' }}>
            <Group gap={4} align="center">
              <IconArrowLeft size={14} style={{ color: 'var(--aviary-muted)' }} />
              <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>{campaign.name || 'Campaign'}</Text>
            </Group>
          </UnstyledButton>
          <Center py={40}>
            <Stack gap="sm" align="center">
              <Text fw={600} style={{ color: 'var(--aviary-ink)' }}>
                No pending candidates for review
              </Text>
              <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
                Hit "Find more prospects" from the campaign to source a new batch.
              </Text>
              <Button variant="light" color="sage" onClick={goBack}>Back to campaign</Button>
            </Stack>
          </Center>
        </Stack>
      </Box>
    );
  }

  const selectedCount = selectedIds.size;
  const strongCount = pending.filter((p) => Number(p.fitScore ?? p.score ?? 0) >= STRONG_FIT_THRESHOLD).length;
  const activeNotes = activeProspect
    ? (notesByProspect[String(activeProspect.externalId)] !== undefined
      ? notesByProspect[String(activeProspect.externalId)]
      : (activeProspect.manualCoachNotes || ''))
    : '';

  return (
    <Box style={{ padding: '32px 24px 120px', maxWidth: 1080, margin: '0 auto' }}>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="baseline" wrap="wrap">
          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 26, lineHeight: 1.25 }}>
              {pending.length} new {pending.length === 1 ? 'candidate' : 'candidates'} for {campaign.name || 'this campaign'}
            </Text>
            <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
              Same review as your first batch — tick who&apos;s in, skip who isn&apos;t. Approved candidates join the campaign.
            </Text>
          </Stack>
          <UnstyledButton onClick={goBack}>
            <Text size="sm" c="sage" fw={600}>← back to campaign</Text>
          </UnstyledButton>
        </Group>

        {/* Pre-tick banner */}
        {strongCount > 0 && (
          <Paper
            p="md"
            radius="md"
            style={{
              background: 'var(--mantine-color-sage-0)',
              border: '1px solid var(--mantine-color-sage-4)',
            }}
          >
            <Group justify="space-between" wrap="nowrap" gap="md">
              <Text size="sm" style={{ color: 'var(--aviary-ink)' }}>
                The {strongCount} strongest are pre-ticked. Add them and keep flying.
              </Text>
              <Button
                color="sage"
                size="sm"
                onClick={handleAddToCampaign}
                loading={adding}
                disabled={adding || selectedCount === 0}
                styles={{ root: { flexShrink: 0 } }}
              >
                Add {selectedCount} to campaign →
              </Button>
            </Group>
          </Paper>
        )}

        {/* Rail + detail */}
        <Group align="flex-start" wrap="nowrap" gap="md">
          <Stack gap="xs" style={{ width: 360, flexShrink: 0 }}>
            <Group justify="space-between" align="center">
              <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>sorted by fit</Text>
              {strongCount > 0 && (
                <UnstyledButton onClick={selectAllStrong}>
                  <Text size="xs" c="sage">select all strong</Text>
                </UnstyledButton>
              )}
            </Group>
            <ScrollArea.Autosize mah={640} type="auto" scrollbars="y" offsetScrollbars>
              <Stack gap={6} style={{ minWidth: 0 }}>
                {pending.map((p) => {
                  const id = String(p.externalId);
                  return (
                    <ProspectRail
                      key={id}
                      prospect={p}
                      isActive={activeId === id}
                      isChecked={selectedIds.has(id)}
                      onSelect={(pp) => setActiveId(String(pp.externalId))}
                      onToggleCheck={toggleCheck}
                    />
                  );
                })}
              </Stack>
            </ScrollArea.Autosize>
          </Stack>

          <Paper p="lg" radius="md" withBorder style={{ flex: 1, background: 'var(--aviary-surface)', minWidth: 0 }}>
            {activeProspect ? (
              <DetailPanel
                prospect={activeProspect}
                notesDraft={activeNotes}
                onNotesChange={onNotesChange}
              />
            ) : (
              <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
                Pick a candidate on the left.
              </Text>
            )}
          </Paper>
        </Group>

        <Group justify="flex-end">
          <Button variant="default" color="gray" onClick={goBack}>Cancel</Button>
          <Button
            color="sage"
            onClick={handleAddToCampaign}
            loading={adding}
            disabled={adding || selectedCount === 0}
          >
            Add {selectedCount} to campaign →
          </Button>
        </Group>
      </Stack>
    </Box>
  );
}
